import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  SecurityOutlined,
  LanguageOutlined,
  HelpOutlineOutlined,
} from '@mui/icons-material';
import { authService } from '../../services/api';
import { loginStart, loginSuccess, loginFailure } from '../../redux/slices/authSlice';
import { fetchMasterData } from '../../redux/slices/masterDataSlice';

const Login = () => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const { isAuthenticated, loading, user, userType } = authState;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    loginId: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    loginId: '',
    password: '',
    general: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const message = localStorage.getItem('loginMessage');
    if (message) {
      setLoginMessage(message);
      localStorage.removeItem('loginMessage');
      setTimeout(() => {
        setLoginMessage('');
      }, 10000);
    }
  }, []);

  const getRedirectRoute = () => {
    if (userType === 'company') {
      return '/home';
    } else if (userType === 'employee' && user?.roles) {
      const roles = user.roles;
      if (roles.includes('BOOKKEEPING')) return '/bookkeeping';
      if (roles.includes('SALES')) return '/sales/estimate-quotation';
      if (roles.includes('CUSTOMER_MANAGEMENT')) return '/customers/manage';
      if (roles.includes('INVENTORY_MANAGEMENT')) return '/inventory';
      if (roles.includes('PURCHASES')) return '/purchases/purchase-order';
      if (roles.includes('FINANCE')) return '/cash-flow';
      if (roles.includes('EMPLOYEES')) return '/employees';
      if (roles.includes('COMPANY_EXPENSES')) return '/company-expenses';
      if (roles.includes('CAR_MANAGEMENT')) return '/car-assignments';
      if (roles.includes('MASTER_DATA')) return '/master-data/payment-methods';
    }
    return '/profile';
  };

  useEffect(() => {
    if (isAuthenticated) {
      const redirectRoute = getRedirectRoute();
      navigate(redirectRoute);
    }
  }, [isAuthenticated, navigate, userType, user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: '',
        general: '',
      });
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ loginId: '', password: '', general: '' });
    dispatch(loginStart());

    try {
      const response = await authService.login({
        loginId: formData.loginId,
        password: formData.password,
      });
      const detectedUserType = response.userType;
      dispatch(loginSuccess({
        token: response.token,
        user: {
          ...response.user,
          privileges: detectedUserType === 'employee' ? (response.user.privileges || []) : [],
        },
        userType: detectedUserType,
      }));
      try {
        await dispatch(fetchMasterData({ force: true })).unwrap();
      } catch (masterDataError) {
        const skippedInFlight =
          masterDataError?.name === 'ConditionError' ||
          String(masterDataError?.message || '').includes('Aborted due to condition');
        if (!skippedInFlight) {
          console.error('Failed to fetch master data:', masterDataError);
        }
      }
    } catch (err) {
      const errorType = err.response?.data?.type;
      const errorMessage = err.response?.data?.error;
      dispatch(loginFailure(errorMessage));
      if (errorType === 'loginId') {
        setErrors({ ...errors, loginId: errorMessage });
      } else if (errorType === 'password') {
        setErrors({ ...errors, password: errorMessage });
      } else {
        setErrors({ ...errors, general: errorMessage || 'An error occurred during login' });
      }
    }
  };

  const accentColor = '#0d4f4f';
  const accentLight = '#105e7a';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Left Panel - Branding */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 50%',
            width: '50%',
            background: `linear-gradient(160deg, ${accentColor} 0%, #0a3a3a 100%)`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            px: 6,
          }}
        >
          {/* Subtle background pattern */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.04,
              backgroundImage: `radial-gradient(circle at 25% 25%, #fff 1px, transparent 1px),
                                radial-gradient(circle at 75% 75%, #fff 1px, transparent 1px)`,
              backgroundSize: '48px 48px',
            }}
          />

          {/* Company Logo */}
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!logoError ? (
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL || ''}/icon.png`}
                alt="Midtronix"
                sx={{
                  height: 72,
                  maxWidth: 280,
                  objectFit: 'contain',
                }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <Typography variant="h4" fontWeight={800} color="#fff">
                Midtronix
              </Typography>
            )}
          </Box>

          {/* Heading */}
          <Typography
            variant="h3"
            fontWeight={800}
            color="#fff"
            align="center"
            sx={{ mb: 2, lineHeight: 1.15, maxWidth: 420 }}
          >
            Your Trusted Digital Solutions Partner
          </Typography>
          <Typography
            variant="body1"
            color="rgba(255,255,255,0.7)"
            align="center"
            sx={{ maxWidth: 380, lineHeight: 1.7, mb: 5 }}
          >
            Surveillance, network solutions, cloud solutions, software development, and hardware repair—
            all in one place to keep your business running smoothly.
          </Typography>

          {/* Decorative color blocks */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 6, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 300 }}>
            {[
              { w: 72, h: 56, color: accentLight, radius: 12 },
              { w: 56, h: 56, color: '#3d8fa8', radius: 12 },
              { w: 72, h: 56, color: 'rgba(255,255,255,0.1)', radius: 12 },
              { w: 56, h: 40, color: 'rgba(255,255,255,0.06)', radius: 8 },
              { w: 88, h: 40, color: 'rgba(255,255,255,0.08)', radius: 8 },
              { w: 56, h: 40, color: 'rgba(255,255,255,0.06)', radius: 8 },
            ].map((block, i) => (
              <Box
                key={i}
                sx={{
                  width: block.w,
                  height: block.h,
                  bgcolor: block.color,
                  borderRadius: `${block.radius}px`,
                }}
              />
            ))}
          </Box>

          {/* Bottom footer */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              px: 4,
              py: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            
            <Typography variant="caption" color="rgba(255,255,255,0.35)">
              v4.2.0
            </Typography>
            
          </Box>
        </Box>
      )}

      {/* Right Panel - Form */}
      <Box
        sx={{
          flex: isMobile ? 1 : '0 0 50%',
          width: isMobile ? '100%' : '50%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          bgcolor: '#fff',
          marginRight: '40px',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 440, px: { xs: 3, sm: 4 } }}>
          {/* Heading */}
          <Typography
            variant="h4"
            fontWeight={700}
            color="#1a1a2e"
            sx={{ mb: 0.5, lineHeight: 1.2 }}
          >
            Log in to your Account
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Welcome back! Please enter your login credentials.
          </Typography>

          {loginMessage && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              {loginMessage}
            </Alert>
          )}
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {errors.general}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Login ID */}
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
              Login ID
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="loginId"
              name="loginId"
              placeholder="Enter your ID"
              autoFocus
              value={formData.loginId}
              onChange={handleChange}
              error={!!errors.loginId}
              helperText={errors.loginId}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8f9fa',
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: accentColor },
                  '&.Mui-focused fieldset': { borderColor: accentColor },
                },
              }}
            />

            {/* Password */}
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              name="password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8f9fa',
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: accentColor },
                  '&.Mui-focused fieldset': { borderColor: accentColor },
                },
              }}
            />

            {/* Sign In Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.4,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: accentLight,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#0d4d62',
                  boxShadow: '0 4px 12px rgba(16,94,122,0.3)',
                },
                '&.Mui-disabled': {
                  bgcolor: '#5a9aaa',
                },
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          {/* Footer help */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
            <HelpOutlineOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" component="span" color="text.secondary">
              Need help?{' '}
              <a
                href="mailto:maaz@midtronix.com"
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: accentColor,
                  textDecoration: 'none',
                }}
                onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                Contact Support
              </a>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login; 